import { useCallback, useEffect, useState } from 'react';
import { fetchRadarHistory, cancelRadarAlert } from './customerRadarApi.js';

function fmtDateTime(value) {
  if (!value) return '';
  const s = String(value).replace('T', ' ');
  return s.slice(0, 16);
}

const ALERT_STATUS = {
  pending: { label: 'Aguardando chegar', cls: 'border-amber-500/40 bg-amber-50 text-amber-900 dark:border-amber-300/30 dark:bg-amber-950/25 dark:text-amber-100' },
  notified: { label: 'Disponível — avisamos você', cls: 'border-emerald-500/45 bg-emerald-50 text-emerald-900 dark:border-emerald-300/30 dark:bg-emerald-950/25 dark:text-emerald-100' },
  cancelled: { label: 'Cancelado', cls: 'border-zinc-300/60 bg-zinc-50 text-zinc-600 dark:border-zinc-600/40 dark:bg-zinc-800/60 dark:text-zinc-300' },
};

/**
 * "Minha atividade" — histórico de acesso/pesquisa e avisos de reposição do cliente.
 * Somente leitura, exceto cancelar um aviso pendente.
 *
 * @param {{ slug: string, token: string }} props
 */
export function CustomerRadarHistory({ slug, token }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [events, setEvents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [busyId, setBusyId] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const d = await fetchRadarHistory(slug, token);
      setEvents(Array.isArray(d?.events) ? d.events : []);
      setAlerts(Array.isArray(d?.alerts) ? d.alerts : []);
    } catch (e) {
      setErr(e?.message || 'Não foi possível carregar a sua atividade.');
    } finally {
      setLoading(false);
    }
  }, [slug, token]);

  useEffect(() => {
    load();
  }, [load]);

  async function cancel(id) {
    setBusyId(id);
    try {
      await cancelRadarAlert(slug, token, id);
      setAlerts((list) => list.map((a) => (a.id === id ? { ...a, status: 'cancelled' } : a)));
    } catch {
      /* silencioso */
    } finally {
      setBusyId(0);
    }
  }

  if (loading) {
    return <p className="mt-6 text-sm text-zinc-500">A carregar a sua atividade…</p>;
  }
  if (err) {
    return (
      <p className="mt-6 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
        {err}
      </p>
    );
  }

  return (
    <div className="mt-6 space-y-8">
      <section>
        <h2 className="text-sm font-bold text-zinc-800 dark:text-zinc-100">Avisos de reposição</h2>
        <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
          Quando você procura algo que está esgotado, avisamos por e-mail assim que o produto voltar ao estoque.
        </p>
        <ul className="mt-4 space-y-2.5">
          {alerts.length === 0 ? (
            <li className="rounded-2xl border border-dashed border-zinc-300 bg-zinc-50/80 px-4 py-6 text-center text-sm text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-400">
              Nenhum aviso ainda. Pesquise um produto esgotado e cuidamos do resto.
            </li>
          ) : (
            alerts.map((a) => {
              const st = ALERT_STATUS[a.status] || ALERT_STATUS.pending;
              return (
                <li
                  key={a.id}
                  className="flex flex-col gap-2 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-900/80 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-zinc-800 dark:text-zinc-100">
                      {a.product_nome || a.term}
                    </p>
                    <p className="mt-0.5 text-[11px] text-zinc-500">
                      Pesquisa: “{a.term}” · {fmtDateTime(a.created_at)}
                      {a.notified_at ? ` · avisado em ${fmtDateTime(a.notified_at)}` : ''}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${st.cls}`}>
                      {st.label}
                    </span>
                    {a.status === 'pending' ? (
                      <button
                        type="button"
                        disabled={busyId === a.id}
                        onClick={() => cancel(a.id)}
                        className="rounded-lg border border-zinc-300 px-2.5 py-1 text-[11px] font-semibold text-zinc-600 hover:bg-zinc-100 disabled:opacity-50 dark:border-zinc-600 dark:text-zinc-300 dark:hover:bg-zinc-800"
                      >
                        Cancelar
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })
          )}
        </ul>
      </section>

      <section>
        <h2 className="text-sm font-bold text-zinc-800 dark:text-zinc-100">Histórico de acesso e buscas</h2>
        <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
          Seus acessos e pesquisas recentes nesta loja (apenas para você).
        </p>
        <ul className="mt-4 space-y-1.5">
          {events.length === 0 ? (
            <li className="rounded-2xl border border-dashed border-zinc-300 bg-zinc-50/80 px-4 py-6 text-center text-sm text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-400">
              Ainda não há atividade registada.
            </li>
          ) : (
            events.map((e, i) => (
              <li
                key={i}
                className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm dark:border-zinc-800 dark:bg-zinc-900/70"
              >
                <div className="min-w-0">
                  {e.type === 'search' ? (
                    <p className="truncate text-zinc-700 dark:text-zinc-200">
                      <span className="font-semibold">Pesquisa:</span> “{e.term}”
                      {e.in_stock === 0 ? (
                        <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-800 dark:text-amber-200">
                          esgotado
                        </span>
                      ) : null}
                    </p>
                  ) : (
                    <p className="text-zinc-700 dark:text-zinc-200">
                      <span className="font-semibold">Acesso à loja</span>
                    </p>
                  )}
                  {e.type === 'search' && e.results != null ? (
                    <p className="text-[11px] text-zinc-400">
                      {e.results} resultado(s) · {e.in_stock ?? 0} em estoque
                    </p>
                  ) : null}
                </div>
                <span className="shrink-0 text-[11px] tabular-nums text-zinc-400">{fmtDateTime(e.at)}</span>
              </li>
            ))
          )}
        </ul>
      </section>
    </div>
  );
}
